import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Loader2,
  RefreshCw,
  MapPin,
  Car,
  Sparkles,
  Inbox,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Franchise-only panel: lists each new quote request once with a
 * per-store distance list and an "Assign" button next to each
 * eligible store. Backend ensures only requests that match ≥2 of the
 * franchise's stores show up here — single-match requests still flow
 * through the per-store dashboard like before.
 *
 * After assignment the request disappears from this list and starts
 * showing in the chosen store's normal Available Requests view (the
 * existing flow handles quoting, accepting, completing, etc.).
 */
export default function FranchiseNewRequestsPanel({ onAssigned }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [assigningKey, setAssigningKey] = useState(null); // `${request_id}:${shop_id}`

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/franchise/new-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load new requests");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAssign = async (requestId, shopId, shopName) => {
    const key = `${requestId}:${shopId}`;
    setAssigningKey(key);
    try {
      await axios.post(
        `${API}/franchise/new-requests/${requestId}/assign`,
        { shop_id: shopId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Assigned to ${shopName}`);
      setRequests((prev) => prev.filter((r) => r.request_id !== requestId));
      if (onAssigned) onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    } finally {
      setAssigningKey(null);
    }
  };

  return (
    <section className="space-y-3" data-testid="franchise-new-requests-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" />
            New Requests
            {requests.length > 0 && (
              <Badge className="bg-primary/15 text-primary border border-primary/30">
                {requests.length} to assign
              </Badge>
            )}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Requests matching multiple stores in your franchise — pick which
            store will handle each one. After assignment the request appears
            in that store&apos;s dashboard only.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="franchise-new-requests-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : requests.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No shared requests waiting for assignment.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <RequestCard
              key={req.request_id}
              req={req}
              assigningKey={assigningKey}
              onAssign={handleAssign}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RequestCard({ req, assigningKey, onAssign }) {
  const veh = req.vehicle_info || {};
  const vehicleLine = [veh.year, veh.make, veh.model].filter(Boolean).join(" ");
  const tyre = req.tyre_specs || {};
  const tyreSize = tyre.width
    ? `${tyre.width}/${tyre.aspect_ratio || tyre.profile || "?"}R${tyre.rim_size || "?"}`
    : null;
  const services = req.selected_services || (req.service_type || "").split("_");

  return (
    <Card
      className="overflow-hidden"
      data-testid={`franchise-request-${req.request_id}`}
    >
      {/* Header strip */}
      <div className="px-4 py-2 border-b bg-muted/40 flex items-center gap-3 flex-wrap">
        <h3 className="font-heading text-sm font-semibold">
          {req.request_number || `#${req.request_id}`}
        </h3>
        {req.time_remaining && (
          <Badge variant="outline" className="text-[10px]">
            {req.time_remaining}
          </Badge>
        )}
        {req.comparison_intent && (
          <Badge
            className={`text-[10px] capitalize ${
              req.comparison_intent === "specific"
                ? "bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/40"
                : req.comparison_intent === "budget"
                ? "bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/40"
                : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40"
            }`}
          >
            {req.comparison_intent === "specific" && "Specific brand"}
            {req.comparison_intent === "budget" && "Budget match"}
            {req.comparison_intent === "open" && "Open to options"}
          </Badge>
        )}
        {(req.address?.suburb || req.address?.city) && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-auto">
            <MapPin className="h-3 w-3" />
            {[req.address.suburb, req.address.city].filter(Boolean).join(", ")}
          </span>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Request details strip */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
          {(veh.rego || vehicleLine) && (
            <span className="inline-flex items-center gap-1.5">
              <Car className="h-3.5 w-3.5 text-muted-foreground" />
              {veh.rego && (
                <span className="font-semibold uppercase tracking-wide">
                  {veh.rego}
                </span>
              )}
              {vehicleLine && <span>{vehicleLine}</span>}
              {veh.lookup_source === "carjam" && (
                <span className="px-1.5 py-0 rounded text-[9px] font-bold tracking-wider bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/40">
                  CARJAM
                </span>
              )}
            </span>
          )}
          {tyreSize && (
            <span>
              <span className="text-muted-foreground">Size</span>{" "}
              <span className="font-semibold">{tyreSize}</span>
            </span>
          )}
          {req.quantity && (
            <span>
              <span className="text-muted-foreground">Qty</span>{" "}
              <span className="font-semibold">{req.quantity}</span>
            </span>
          )}
          {req.price_range && (
            <span className="capitalize">
              <span className="text-muted-foreground">Budget</span>{" "}
              <span className="font-medium">{req.price_range}</span>
            </span>
          )}
          {services?.length > 0 && (
            <span className="capitalize">
              <span className="text-muted-foreground">Services</span>{" "}
              <span className="font-medium">{services.join(", ").replace(/_/g, " ")}</span>
            </span>
          )}
        </div>

        {req.customer_note && (
          <p className="text-xs italic text-amber-800 dark:text-amber-200 bg-amber-500/10 border-l-2 border-amber-500/60 px-2 py-1">
            “{req.customer_note}”
          </p>
        )}

        {/* Eligible stores list */}
        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Eligible stores · choose one to assign
          </p>
          {req.eligible_stores.map((s) => {
            const key = `${req.request_id}:${s.shop_id}`;
            const isAssigning = assigningKey === key;
            return (
              <div
                key={s.shop_id}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 hover:bg-muted/50 transition-colors"
                data-testid={`franchise-eligible-store-${s.shop_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.shop_name}</p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span className="font-semibold text-foreground">
                      {s.distance_km.toFixed(1)} km
                    </span>
                    {s.google_rating != null && (
                      <span>★ {s.google_rating.toFixed(1)}</span>
                    )}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => onAssign(req.request_id, s.shop_id, s.shop_name)}
                  disabled={!!assigningKey}
                  className="bg-primary text-primary-foreground h-8 text-xs"
                  data-testid={`assign-store-${req.request_id}-${s.shop_id}`}
                >
                  {isAssigning ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Assigning…
                    </>
                  ) : (
                    <>
                      Assign
                      <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
